import {useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {CurrencyCodeType} from 'shared/web';

import {ChartVariant} from '../types/ChartVariant';
import {formatChartPriceLabel, formatChartPriceThousandsTick} from '../utils/formatChartPriceLabel';

export type Formatter = (value: number) => string;

export function useYAxisReferenceLineLabelFormatter(
  chartVariant: ChartVariant,
  currencyCode: CurrencyCodeType
) {
  const formatNumber = useFormatNumber();

  const formatPriceLabel: Formatter = (value) =>
    formatChartPriceLabel(value, currencyCode, {formatNumber});

  return match<ChartVariant, Formatter>(chartVariant)
    .with('price', () => formatPriceLabel)
    .with('mileage', () => (value) => formatNumber(Math.round(value / 1000)) ?? '')
    .with('daysOnStock', () => (value) => formatNumber(value) ?? '')
    .with('featuresLevel', () => (value) => formatNumber(Math.floor(value)) ?? '')
    .with('priceMap', () => formatPriceLabel)
    .with('mapView', () => () => '')
    .exhaustive();
}

export function useYAxisMobileTickFormatter(
  chartVariant: ChartVariant,
  currencyCode: CurrencyCodeType
) {
  const labelFormatter = useYAxisReferenceLineLabelFormatter(chartVariant, currencyCode);
  const formatNumber = useFormatNumber();
  const addSuffix = (number: string | null, unit: string) => `${number ?? ''}${unit}`;
  const formatPriceThousandsTick = (value: number, suffix = '') =>
    formatChartPriceThousandsTick(value, currencyCode, {formatNumber}, suffix);

  return match<ChartVariant, Formatter>(chartVariant)
    .with('price', () => (value) => formatPriceThousandsTick(value, ' K'))
    .with('priceMap', () => (value) => formatPriceThousandsTick(value, ' K'))
    .with('mileage', () => (value) => addSuffix(labelFormatter(value), ' K'))
    .with('featuresLevel', () => (value) => addSuffix(labelFormatter(value), ' %'))
    .otherwise(() => labelFormatter);
}

export function useYAxisDesktopTickFormatter(
  chartVariant: ChartVariant,
  currencyCode: CurrencyCodeType
) {
  const labelFormatter = useYAxisReferenceLineLabelFormatter(chartVariant, currencyCode);
  const formatNumber = useFormatNumber();
  const formatPriceThousandsTick = (value: number) =>
    formatChartPriceThousandsTick(value, currencyCode, {formatNumber});

  return match<ChartVariant, Formatter>(chartVariant)
    .with('price', () => formatPriceThousandsTick)
    .with('priceMap', () => formatPriceThousandsTick)
    .otherwise(() => labelFormatter);
}
